import React from 'react';
import { createRoot } from 'react-dom/client';
import ComponentRegistry from '../components/ComponentRegistry';

// Function to dynamically load component from server
const loadComponentFromServer = async (componentName: string, props: any) => {
  try {
    console.log(`[DynamicLoader] Fetching component source for: ${componentName}`);
    const response = await fetch(`/calculators/component_source/${componentName}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch component: ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.success || !data.code) {
      throw new Error('Component source not available');
    }

    // Transpile and execute the component code
    let originalCode = data.code;

    // Extract all React imports to know what hooks are being used
    const reactImportMatch = originalCode.match(/import\s+React(?:\s*,\s*\{([^}]*)\})?\s+from\s+['"]react['"];?/);
    const hooksImportMatch = originalCode.match(/import\s+\{([^}]+)\}\s+from\s+['"]react['"];?/);

    let importedHooks = [];
    if (reactImportMatch && reactImportMatch[1]) {
      importedHooks = reactImportMatch[1].split(',').map(h => h.trim()).filter(Boolean);
    }
    if (hooksImportMatch) {
      importedHooks = [...importedHooks, ...hooksImportMatch[1].split(',').map(h => h.trim()).filter(Boolean)];
    }

    // Remove duplicates
    importedHooks = [...new Set(importedHooks)];

    if (importedHooks.length > 0) {
      console.log(`[DynamicLoader] Detected React imports: ${importedHooks.join(', ')}`);
    }

    // Extract lucide-react imports
    const lucideImportMatch = originalCode.match(/import\s+\{([^}]+)\}\s+from\s+['"]lucide-react['"];?/);
    let importedIcons = [];
    if (lucideImportMatch) {
      importedIcons = lucideImportMatch[1].split(',').map(icon => icon.trim()).filter(Boolean);
      console.log(`[DynamicLoader] Detected lucide-react imports: ${importedIcons.join(', ')}`);
    }

    // Extract recharts imports
    const rechartsImportMatch = originalCode.match(/import\s+\{([^}]+)\}\s+from\s+['"]recharts['"];?/);
    let importedRechartsComponents = [];
    if (rechartsImportMatch) {
      importedRechartsComponents = rechartsImportMatch[1].split(',').map(comp => comp.trim()).filter(Boolean);
      console.log(`[DynamicLoader] Detected recharts imports: ${importedRechartsComponents.join(', ')}`);
    }

    // Find the exported component name BEFORE any transformations
    let exportedComponentName = null;

    // Check for: export default ComponentName;
    const defaultExportMatch = originalCode.match(/export\s+default\s+(\w+);?\s*$/m);
    if (defaultExportMatch) {
      exportedComponentName = defaultExportMatch[1];
      console.log(`[DynamicLoader] Found default export: ${exportedComponentName}`);
    }

    // Check for: export default function ComponentName
    const defaultFunctionMatch = originalCode.match(/export\s+default\s+function\s+(\w+)/);
    if (defaultFunctionMatch && !exportedComponentName) {
      exportedComponentName = defaultFunctionMatch[1];
      console.log(`[DynamicLoader] Found default function export: ${exportedComponentName}`);
    }

    // Check for: export default const ComponentName
    const defaultConstMatch = originalCode.match(/export\s+default\s+const\s+(\w+)/);
    if (defaultConstMatch && !exportedComponentName) {
      exportedComponentName = defaultConstMatch[1];
      console.log(`[DynamicLoader] Found default const export: ${exportedComponentName}`);
    }

    if (!exportedComponentName) {
      throw new Error('Could not find exported component name in the code');
    }

    // Check if Babel is available
    if (typeof window.Babel === 'undefined') {
      console.log('[DynamicLoader] Loading Babel standalone...');

      // Load Babel dynamically
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/@babel/standalone/babel.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    // First, transpile the ORIGINAL code with Babel (handles TypeScript, JSX, etc.)
    console.log(`[DynamicLoader] Transpiling with Babel...`);
    let transpiled;
    try {
      transpiled = window.Babel.transform(originalCode, {
        presets: ['react', 'typescript'],
        filename: `${componentName}.tsx`
      }).code;
    } catch (babelError) {
      console.error('[DynamicLoader] Babel transpilation error:', babelError);
      throw new Error(`Babel transpilation failed: ${babelError.message}`);
    }

    console.log(`[DynamicLoader] Code transpiled successfully`);

    // NOW remove imports and exports from the transpiled JavaScript
    transpiled = transpiled
      .replace(/import\s+React(?:\s*,\s*\{[^}]*\})?\s+from\s+['"]react['"];?/g, '')
      .replace(/import\s+\{([^}]+)\}\s+from\s+['"]react['"];?/g, '')
      .replace(/import\s+\{([^}]+)\}\s+from\s+['"]lucide-react['"];?/g, '')
      .replace(/import\s+\{([^}]+)\}\s+from\s+['"]recharts['"];?/g, '')
      .replace(/import\s+.*?from\s+['"].*?['"];?/g, '')
      .replace(/export\s+default\s+\w+;?\s*$/gm, '')
      .replace(/export\s+default\s+/g, '')
      .replace(/export\s+(const|function|class|let|var)\s+/g, '$1 ');

    // Build the list of all common React hooks to make available
    const allReactHooks = [
      'useState', 'useEffect', 'useContext', 'useReducer', 'useCallback',
      'useMemo', 'useRef', 'useImperativeHandle', 'useLayoutEffect', 'useDebugValue',
      'useDeferredValue', 'useTransition', 'useId', 'useSyncExternalStore',
      'useInsertionEffect'
    ];

    // Create destructuring statements for all hooks
    const hookDestructuring = allReactHooks.map(hook => `const ${hook} = React.${hook};`).join('\n');

    // Load lucide-react dynamically only if needed
    let LucideIcons = {};
    if (importedIcons.length > 0) {
      console.log('[DynamicLoader] Loading lucide-react icons...');
      LucideIcons = await import('lucide-react');
      console.log('[DynamicLoader] Lucide icons loaded');
    }

    // Load recharts dynamically only if needed
    let Recharts = {};
    if (importedRechartsComponents.length > 0) {
      console.log('[DynamicLoader] Loading recharts...');
      Recharts = await import('recharts');
      console.log('[DynamicLoader] Recharts loaded');
    }

    // Create constants for imported lucide icons
    const iconDestructuring = importedIcons.length > 0
      ? importedIcons.map(icon => `const ${icon} = LucideIcons.${icon};`).join('\n')
      : '';

    // Create constants for imported recharts components
    const rechartsDestructuring = importedRechartsComponents.length > 0
      ? importedRechartsComponents.map(comp => `const ${comp} = Recharts.${comp};`).join('\n')
      : '';

    // Create wrapper that properly scopes the component and returns it
    const wrappedCode = `
      'use strict';
      ${hookDestructuring}
      ${iconDestructuring}
      ${rechartsDestructuring}
      ${transpiled}
      if (typeof ${exportedComponentName} === 'undefined') {
        throw new Error('Component ${exportedComponentName} is not defined after execution');
      }
      return ${exportedComponentName};
    `;

    // Create a function that returns the component with React, LucideIcons, and Recharts provided
    const componentFunction = new Function(
      'React',
      'LucideIcons',
      'Recharts',
      wrappedCode
    );

    // Execute the function with React, LucideIcons, and Recharts context
    const Component = componentFunction(React, LucideIcons, Recharts);

    console.log(`[DynamicLoader] Component type:`, typeof Component);

    if (!Component || typeof Component !== 'function') {
      throw new Error(`Component ${exportedComponentName} is not a valid React component (got ${typeof Component})`);
    }

    console.log(`[DynamicLoader] Successfully loaded component: ${componentName}`);
    return <Component {...props} />;
  } catch (error) {
    console.error(`[DynamicLoader] Error loading component ${componentName}:`, error);
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-red-800 to-red-900 flex items-center justify-center p-8">
        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 max-w-md">
          <h2 className="text-2xl font-bold text-white mb-4">Component Load Error</h2>
          <p className="text-gray-300 mb-2">Failed to load calculator: {componentName}</p>
          <pre className="text-red-400 text-sm bg-black/30 p-4 rounded overflow-auto max-h-64">
            {error instanceof Error ? error.message : String(error)}
          </pre>
        </div>
      </div>
    );
  }
};

document.addEventListener('DOMContentLoaded', async () => {
  console.log('[ComponentLoader] DOMContentLoaded event fired');

  try {
    const container = document.getElementById('react-root');
    console.log('[ComponentLoader] Container found:', !!container);

    if (container) {
      // Get calculator data from data attributes
      const calculatorId = container.dataset.calculatorId;
      const calculatorName = container.dataset.calculatorName || '';
      console.log('[ComponentLoader] Calculator:', calculatorName, 'ID:', calculatorId);

      const root = createRoot(container);
      console.log('[ComponentLoader] Root created');

      // Check if component exists in registry
      const registeredComponent = ComponentRegistry[calculatorName];
      console.log('[ComponentLoader] Registered component found:', !!registeredComponent);

      if (registeredComponent) {
        console.log(`[ComponentLoader] Using registered component: ${calculatorName}`);
        const CalculatorComponent = registeredComponent;
        root.render(
          <CalculatorComponent
            calculatorId={calculatorId}
            calculatorName={calculatorName}
          />
        );
        console.log('[ComponentLoader] Registered component rendered');
      } else {
        console.log(`[ComponentLoader] Component not in registry, loading from server: ${calculatorName}`);
        // Show loading state
        root.render(
          <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 flex items-center justify-center">
            <div className="text-white text-xl">Loading {calculatorName}...</div>
          </div>
        );

        // Load component from server
        const dynamicComponent = await loadComponentFromServer(calculatorName, {
          calculatorId,
          calculatorName
        });

        root.render(dynamicComponent);
        console.log('[ComponentLoader] Dynamic component rendered');
      }
    }
  } catch (error) {
    console.error('[ComponentLoader] Fatal error:', error);
    const container = document.getElementById('react-root');
    if (container) {
      container.innerHTML = `
        <div style="min-height: 100vh; background: linear-gradient(to bottom right, #7f1d1d, #991b1b, #7f1d1d); display: flex; align-items: center; justify-content: center; padding: 2rem;">
          <div style="background: rgba(255, 255, 255, 0.1); backdrop-filter: blur(10px); border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 1rem; padding: 2rem; max-width: 32rem;">
            <h2 style="font-size: 1.5rem; font-weight: bold; color: white; margin-bottom: 1rem;">Application Error</h2>
            <p style="color: #d1d5db; margin-bottom: 0.5rem;">Failed to initialize calculator</p>
            <pre style="color: #fca5a5; font-size: 0.875rem; background: rgba(0, 0, 0, 0.3); padding: 1rem; border-radius: 0.5rem; overflow: auto; max-height: 16rem;">
${error instanceof Error ? error.message : String(error)}
${error instanceof Error && error.stack ? '\n' + error.stack : ''}
            </pre>
          </div>
        </div>
      `;
    }
  }
});